import { defineStore } from 'pinia'
import * as authApi from '@/api/auth'
import * as userApi from '@/api/user'

/**
 * VIP 信息（与后端对齐，字段可选，缺省由 sanitize 补 '-'）
 */
interface VipInfo {
  level?: number | string
  name?: string
  expireTime?: string | number
  benefits?: string[]
}

/**
 * 用户信息 Store（完整 profile 字段，与后端 UserFullVO / UserCardVO / LoginUserVO 对齐）
 */
interface UserState {
  // 标识
  userId: number | null
  tenantId: number | null
  oid: string
  // 基础信息
  userName: string
  nickname: string
  avatar: string
  mobile?: string
  role: string
  status?: number | string
  gender?: number | string
  // 档案字段
  birthday?: string
  age?: number | string
  stature?: number | string
  education?: string | number
  salary?: string | number
  marriage?: string | number
  city?: string
  intro?: string
  signature?: string
  // ============ 择偶条件（8 项，严格对齐后端 partner_* / MatchPreferenceDTO）============
  partnerAgeFrom?: number
  partnerAgeTo?: number
  partnerStatureFrom?: number
  partnerStatureTo?: number
  partnerMinEducation?: number | string
  partnerMinSalary?: number | string
  partnerMarriagePref?: number | string | (number | string)[]
  partnerCity?: string
  // 认证 / VIP
  isVerified?: boolean | string
  vipLevel?: number | string
  vipInfo?: VipInfo
  // 时间
  createTime?: string
  // Token
  accessToken: string
  refreshToken: string
  expireTime?: number
}

const STORAGE_KEY_ACCESS = 'aries_access_token'
const STORAGE_KEY_REFRESH = 'aries_refresh_token'

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    userId: null,
    tenantId: null,
    oid: '',
    userName: '',
    nickname: '',
    avatar: '',
    mobile: undefined,
    role: '',
    status: undefined,
    gender: undefined,
    birthday: undefined,
    age: undefined,
    stature: undefined,
    education: undefined,
    salary: undefined,
    marriage: undefined,
    city: undefined,
    intro: undefined,
    signature: undefined,
    // 择偶条件 8 项（默认 undefined，sanitize 统一 '-' 兜底）
    partnerAgeFrom: undefined,
    partnerAgeTo: undefined,
    partnerStatureFrom: undefined,
    partnerStatureTo: undefined,
    partnerMinEducation: undefined,
    partnerMinSalary: undefined,
    partnerMarriagePref: undefined,
    partnerCity: undefined,
    isVerified: undefined,
    vipLevel: undefined,
    vipInfo: undefined,
    createTime: undefined,
    accessToken: localStorage.getItem(STORAGE_KEY_ACCESS) || '',
    refreshToken: localStorage.getItem(STORAGE_KEY_REFRESH) || '',
    expireTime: undefined
  }),

  getters: {
    isLogin: (s) => !!s.accessToken,
    isMerchant: (s) => s.role === 'MERCHANT',
    isAdmin: (s) => s.role === 'ADMIN'
  },

  actions: {
    setTokens(access: string, refresh: string) {
      this.accessToken = access
      this.refreshToken = refresh
      localStorage.setItem(STORAGE_KEY_ACCESS, access)
      localStorage.setItem(STORAGE_KEY_REFRESH, refresh)
    },
    /**
     * 拉取当前登录用户完整档案（/user/me）并写入 state
     */
    async fetchMe() {
      const res = await userApi.getMe()
      const data = res.data
      if (!data) return
      // 写回各字段：字段不存在时 sanitize 已转 '-'，这里直接赋值
      // 后端 UserFullVO / /user/me 标准字段：userId / id（兼容两种命名）
      const rawUserId = data.userId ?? data.id
      if (typeof rawUserId !== 'undefined' && rawUserId !== null && rawUserId !== '-') {
        this.userId = Number(rawUserId) || null
      }
      if (typeof data.oid !== 'undefined') this.oid = String(data.oid)
      if (typeof data.userName !== 'undefined') {
        this.userName = String(data.userName)
        // 昵称默认 = userName
        this.nickname = this.nickname || String(data.userName)
      }
      if (typeof data.avatar !== 'undefined') this.avatar = String(data.avatar)
      if (typeof data.gender !== 'undefined') this.gender = data.gender
      if (typeof data.birthday !== 'undefined') this.birthday = String(data.birthday)
      if (typeof data.age !== 'undefined') this.age = data.age
      if (typeof data.stature !== 'undefined') this.stature = data.stature
      if (typeof data.education !== 'undefined') this.education = data.education as any
      if (typeof data.salary !== 'undefined') this.salary = data.salary as any
      if (typeof data.marriage !== 'undefined') this.marriage = data.marriage as any
      if (typeof data.city !== 'undefined') this.city = String(data.city)
      if (typeof data.intro !== 'undefined') this.intro = String(data.intro)
      if (typeof data.signature !== 'undefined') this.signature = String(data.signature)
      if (typeof data.vipLevel !== 'undefined') this.vipLevel = data.vipLevel
      if (typeof data.vipStatus !== 'undefined') this.vipLevel = Number(data.vipStatus)
      if (typeof data.isVerified !== 'undefined') this.isVerified = data.isVerified as any
      if (typeof data.createTime !== 'undefined') this.createTime = String(data.createTime)

      // ========= 择偶条件 8 项：兼容 camelCase / snake_case / profile 嵌套对象 =========
      const sources: any[] = [data, (data as any).profile || {}]
      const pick = (keys: string[]): any => {
        for (const src of sources) {
          for (const k of keys) {
            const v = src[k]
            if (typeof v !== 'undefined' && v !== null && v !== '' && v !== '-') return v
          }
        }
        return undefined
      }
      const pickNum = (keys: string[]): number | undefined => {
        const raw = pick(keys)
        if (raw === undefined) return undefined
        const n = Number(raw)
        return Number.isFinite(n) && !Number.isNaN(n) ? n : undefined
      }
      const pickStr = (keys: string[]): string | undefined => {
        const raw = pick(keys)
        return raw === undefined ? undefined : String(raw)
      }

      const ageFrom = pickNum(['partnerAgeFrom', 'partner_age_from'])
      if (ageFrom !== undefined) this.partnerAgeFrom = ageFrom
      const ageTo = pickNum(['partnerAgeTo', 'partner_age_to'])
      if (ageTo !== undefined) this.partnerAgeTo = ageTo
      const stFrom = pickNum(['partnerStatureFrom', 'partner_stature_from'])
      if (stFrom !== undefined) this.partnerStatureFrom = stFrom
      const stTo = pickNum(['partnerStatureTo', 'partner_stature_to'])
      if (stTo !== undefined) this.partnerStatureTo = stTo
      const minEdu = pick(['partnerMinEducation', 'partner_education_min', 'partnerEducationMin'])
      if (minEdu !== undefined) this.partnerMinEducation = minEdu as any
      const minSal = pick(['partnerMinSalary', 'partner_salary_min', 'partnerSalaryMin'])
      if (minSal !== undefined) this.partnerMinSalary = minSal as any
      const marriage = pick(['partnerMarriagePref', 'partner_marriage', 'partnerMarriage'])
      if (marriage !== undefined) this.partnerMarriagePref = marriage as any
      const city = pickStr(['partnerCity', 'partner_city'])
      if (city !== undefined) this.partnerCity = city
    },
    /**
     * 登录 + 拉取档案合并操作（login.vue / register.vue 直接调用）
     */
    async loginAndFetch(mobile: string, password: string) {
      // 1. 密码登录（type:password 作为后端登录类型标识；DTO 未声明字段用 as any 兼容避免 TS 报错，不影响运行）
      const loginRes = await authApi.login(
        { mobile, password, type: 'password' } as Parameters<typeof authApi.login>[0]
      )
      const tokenData = loginRes.data
      if (!tokenData) throw new Error('登录返回空')

      // 2. 写 token（若 login 返回了内嵌 user，先填一版基础字段作为兜底）
      this.setTokens(tokenData.accessToken, tokenData.refreshToken)
      if (tokenData.expiresIn) {
        this.expireTime = Date.now() + Number(tokenData.expiresIn) * 1000
      }
      const innerUser = tokenData.user
      if (innerUser) {
        // 兼容后端两种字段命名
        const rawUid = innerUser.userId ?? innerUser.id
        if (typeof rawUid !== 'undefined' && rawUid !== null && rawUid !== '-') {
          this.userId = Number(rawUid) || null
        }
        if (typeof innerUser.oid !== 'undefined') this.oid = String(innerUser.oid)
        if (typeof innerUser.userName !== 'undefined') {
          this.userName = String(innerUser.userName)
          this.nickname = this.nickname || String(innerUser.userName)
        }
        if (typeof innerUser.avatar !== 'undefined') this.avatar = String(innerUser.avatar)
        if (typeof innerUser.role !== 'undefined') this.role = String(innerUser.role)
        if (typeof innerUser.gender !== 'undefined') this.gender = innerUser.gender
        // vip 状态兼容：后端有时返回 vipStatus 有时返回 vipLevel，兜底映射
        if (typeof innerUser.vipLevel !== 'undefined') this.vipLevel = Number(innerUser.vipLevel)
        if (typeof innerUser.vipStatus !== 'undefined') this.vipLevel = Number(innerUser.vipStatus)
      }

      // 3. 拉取完整档案（失败不阻断登录，因为 token 已写入）
      try {
        await this.fetchMe()
      } catch (_e) {
        // fetchMe 失败：request 层已 toast，这里静默继续
      }
    },
    logout() {
      this.userId = null
      this.tenantId = null
      this.oid = ''
      this.userName = ''
      this.nickname = ''
      this.avatar = ''
      this.mobile = undefined
      this.role = ''
      this.status = undefined
      this.gender = undefined
      this.birthday = undefined
      this.age = undefined
      this.stature = undefined
      this.education = undefined
      this.salary = undefined
      this.marriage = undefined
      this.city = undefined
      this.intro = undefined
      this.signature = undefined
      // 择偶条件 8 项登出清零
      this.partnerAgeFrom = undefined
      this.partnerAgeTo = undefined
      this.partnerStatureFrom = undefined
      this.partnerStatureTo = undefined
      this.partnerMinEducation = undefined
      this.partnerMinSalary = undefined
      this.partnerMarriagePref = undefined
      this.partnerCity = undefined
      this.isVerified = undefined
      this.vipLevel = undefined
      this.vipInfo = undefined
      this.createTime = undefined
      this.accessToken = ''
      this.refreshToken = ''
      this.expireTime = undefined
      localStorage.removeItem(STORAGE_KEY_ACCESS)
      localStorage.removeItem(STORAGE_KEY_REFRESH)
    }
  }
})
